import { ZTooltip } from '@/shared/components/ZTooltip/ZTooltip';
import { addNotification } from '@/shared/states/notification';
import { Select, SelectItem, Spinner } from '@nextui-org/react';
import { useQueryClient } from '@tanstack/react-query';
import { GripHorizontal, Info, Trash } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FIELD_TYPE_OPTIONS } from './constants';
import { useFetchResponseSchemaQuery } from './hooks';
import { useStepFourAutoSave } from './hooks/useStepFourAutoSave';
import { useFrameworkWizardStore } from './states';
import { FrameworkField } from './types';

const DraggableHeader = ({
  title,
  hasInfo,
  isEditable,
  isDeletable,
  showHandle = true,
  locked = false,
  fieldId,
  onTitleChange,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  usedFieldTypes = [], // NEW PROP
  isDeleting = false,
  hasTestingProcedures = true, // Default to true for backward compatibility
}: {
  title: string;
  hasInfo?: boolean;
  isEditable?: boolean;
  isDeletable?: boolean;
  showHandle?: boolean;
  locked?: boolean;
  fieldId?: string;
  onTitleChange?: (newTitle: string) => void;
  onDelete?: () => void;
  onDragStart?: (fieldId: string) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (targetFieldId: string) => void;
  usedFieldTypes?: string[]; // NEW PROP
  isDeleting?: boolean;
  hasTestingProcedures?: boolean; // Add prop for testing procedures
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedFieldType, setSelectedFieldType] = useState<Set<string>>(new Set());
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Simple exact match detection
  const getFieldTypeFromTitle = (title: string) => {
    const matchingOption = FIELD_TYPE_OPTIONS.find(
      (option) => option.label === title
    );
    return matchingOption?.value || '';
  };

  // Open the select dropdown when editing starts
  useEffect(() => {
    if (isEditing) {
      // Open the dropdown immediately when editing starts
      setIsSelectOpen(true);
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    if (isEditable && onTitleChange) {
      // Auto-detect and set the field type when starting to edit
      const detectedType = getFieldTypeFromTitle(title);
      setSelectedFieldType(detectedType ? new Set([detectedType]) : new Set());
      setIsEditing(true);
    }
  };

  const handleTitleSubmit = () => {
    setIsEditing(false);
    if (onTitleChange) {
      onTitleChange(editTitle);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditTitle(title);
      setSelectedFieldType(new Set());
    }
  };

  const handleSelectionChange = (keys: any) => {
    const selectedValue = Array.from(keys)[0] as string;

    if (selectedValue) {
      const selectedOption = FIELD_TYPE_OPTIONS.find(
        (option) => option.value === selectedValue
      );
      if (selectedOption && onTitleChange) {
        setEditTitle(selectedOption.label);
        setSelectedFieldType(new Set([selectedValue]));
        onTitleChange(selectedOption.label);
        setIsSelectOpen(false);
        setIsEditing(false);
      }
    } else {
      // Handle case where same item is selected again (keys will be empty)
      // Keep the current selection and just close the dropdown
      setIsSelectOpen(false);
      setIsEditing(false);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (fieldId && onDragStart) {
      onDragStart(fieldId);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (onDragOver) {
      onDragOver(e);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (fieldId && onDrop) {
      onDrop(fieldId);
    }
  };

  return (
    <div
      className="sticky top-0 z-20 flex flex-row items-center py-1.5 px-0.5 gap-0.5 w-full h-11 bg-[#F9F7F7] border-b border-[#E4E4E7]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center w-5">
        {' '}
        {/* Fixed width container for handle */}
        {showHandle && (
          <button
            className={`p-0.5 transition-opacity duration-200 cursor-grab active:cursor-grabbing ${isHovered ? 'opacity-100' : 'opacity-0'}`}
            draggable
            onDragStart={handleDragStart}
            onDragEnd={onDragEnd}
          >
            <GripHorizontal size={16} className="text-[#71717A] -rotate-90" />
          </button>
        )}
      </div>

      <div
        className={`flex flex-row justify-center items-center gap-1 flex-grow h-8 rounded ${isEditable && isEditing
          ? 'bg-white border border-[#E4E4E7] cursor-text'
          : isEditable
            ? 'cursor-pointer hover:bg-gray-50'
            : ''
          }`}
        onClick={handleTitleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isEditing ? (
          <Select
            size="sm"
            placeholder="Select Response Type"
            isDisabled={false}
            selectedKeys={selectedFieldType}
            onSelectionChange={handleSelectionChange}
            onClose={() => {
              setIsSelectOpen(false);
              setIsEditing(false);
            }}
            onOpenChange={(isOpen) => setIsSelectOpen(isOpen)}
            isOpen={isSelectOpen}
            classNames={{
              mainWrapper: 'w-full',
              trigger:
                'min-h-unit-6 h-6 border-0 shadow-none bg-transparent hover:bg-transparent data-[hover=true]:bg-transparent',
              popoverContent: 'w-[250px] shadow-none border-1 border-gray-300',
            }}
            autoFocus
          >
            {FIELD_TYPE_OPTIONS
              .filter(option =>
                // Filter out test_results option if there are no testing procedures
                (hasTestingProcedures || option.value !== 'test_results')
              )
              .map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  isDisabled={usedFieldTypes.includes(option.value)}
                >
                  <div className="flex items-center w-full pointer-events-none">
                    <span style={{ pointerEvents: 'auto' }}>
                      <ZTooltip
                        isDisabled={false}
                        content={option.tooltip}
                        placement="left"
                        showArrow
                        className="max-w-xs whitespace-pre-line"
                      >
                        <Info
                          size={14}
                          className="text-[#A5A5A5] mr-2 flex-shrink-0"
                        />
                      </ZTooltip>
                    </span>
                    <span>{option.label}</span>
                  </div>
                </SelectItem>
              ))}
          </Select>
        ) : (
          <span className="text-xs font-semibold text-[#71717A] flex-grow">
            {editTitle || 'Select Response Type'}
          </span>
        )}
      </div>

      {hasInfo && (
        <ZTooltip
          content="Lorem Ipsum is simply dummy text of the printing and typesetting industry."
          placement="bottom"
          isDisabled={false}
        >
          <button className="p-0.5">
            <Info size={16} className="text-[#A5A5A5]" />
          </button>
        </ZTooltip>
      )}


      <div className="flex items-center w-5">
        {/* Fixed width container for delete button */}
        {isDeletable && (
          <button
            className={`p-0.5 transition-opacity duration-200 ${isDeleting ? 'opacity-100' : isHovered ? 'opacity-100' : 'opacity-0'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (onDelete && !isDeleting) onDelete();
            }}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Spinner size='sm' classNames={{ wrapper: "h-4 w-4" }} />
            ) : (
              <Trash size={16} className="text-[#F31260]" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const TableCell = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`flex flex-row justify-center items-start gap-1 w-full h-24 border-b border-[#E4E4E7] ${className}`}
  >
    {children}
  </div>
);

const Column = ({
  header,
  children,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col items-start min-w-[250px] flex-1 h-full border-r border-[#E4E4E7]">
    {header}
    {children}
  </div>
);

// Editable header for columns (StepOne style)
const EditableHeader = ({
  fieldId,
  value,
  isEditing,
  onEditStart,
  onEditEnd,
  onEditChange,
}: {
  fieldId: string;
  value: string;
  isEditing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onEditChange: (v: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);
  return (
    <div className="sticky top-0 z-20 flex flex-row items-center py-1.5 px-0.5 gap-0.5 w-full h-11 bg-[#F9F7F7] border-b border-[#E4E4E7]">
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onEditChange(e.target.value)}
          onBlur={onEditEnd}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onEditEnd();
            if (e.key === 'Escape') onEditEnd();
          }}
          className="text-xs font-semibold text-[#71717A] outline-none border border-[#E4E4E7] rounded px-1 w-full"
        />
      ) : (
        <span
          className="text-xs font-semibold text-[#71717A] cursor-pointer"
          onClick={onEditStart}
        >
          {value || ''}
        </span>
      )}
    </div>
  );
};

export default function StepFour() {
  const { stepThreeData, stepFourData, updateStepFourData, stepTwoData } =
    useFrameworkWizardStore();
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);
  const [responseFields, setResponseFields] = useState<FrameworkField[]>([]);
  const [isAddingField, setIsAddingField] = useState(false);
  const [isDeletingField, setIsDeleting] = useState(false);
  const [activeDeletingFieldId, setActiveDeletingFieldId] = useState<string | null>(null);
  const isReorderingRef = useRef(false);

  // Check if testing procedures field exists in schema
  const hasTestingProcedures = useMemo(() => {
    // First check if schemaFields exist in stepTwoData
    if (stepTwoData.schemaFields && stepTwoData.schemaFields.length > 0) {
      return stepTwoData.schemaFields.some(
        (f) => f.key === 'testing_procedures' || f.key.toLowerCase().includes('procedure')
      );
    }

    // If we don't have schema fields, check if any control has testing procedures
    if (stepThreeData.controls && stepThreeData.controls.length > 0) {
      const firstControl = stepThreeData.controls[0];
      return Object.keys(firstControl).some(
        (key) => key === 'testing_procedures' || key.toLowerCase().includes('procedure')
      );
    }

    return false;
  }, [stepTwoData.schemaFields, stepThreeData.controls]);

  // Add query client for invalidation
  const queryClient = useQueryClient();

  // Add the auto-save hook
  const { saveNow, isSaving } = useStepFourAutoSave();

  // Get controls from stepThreeData FIRST
  const controls = stepThreeData.controls || [];

  // Fetch response schema data from the URL provided by Step Three
  const {
    data: fetchedResponseSchemaData,
    isSuccess: isResponseSchemaSuccess,
    refetch: refetchResponseSchema,
  } = useFetchResponseSchemaQuery(
    stepFourData.responseSchemaUrl,
    !!stepFourData.responseSchemaUrl
  );

  // Transform response schema data similar to how we handle controls data
  const transformedResponseFields = useMemo(() => {
    if (
      !isResponseSchemaSuccess ||
      !fetchedResponseSchemaData ||
      !Array.isArray(fetchedResponseSchemaData)
    ) {
      return [];
    }

    // Extract field names from the first item in the response schema
    const firstItem = fetchedResponseSchemaData[0];
    if (!firstItem) return [];

    // Get the original controls structure to determine which fields are from Step 3
    const originalControlsFields =
      controls.length > 0 ? Object.keys(controls[0]) : [];

    return Object.keys(firstItem).map((key, index) => {
      // Check if this field exists in the original controls structure
      const isFromStepThree =
        originalControlsFields.includes(key) ||
        originalControlsFields.some(
          (originalKey) => originalKey.toLowerCase() === key.toLowerCase()
        );

      const isReferences = key.toLowerCase() === 'references';
      const isObservations = key.toLowerCase() === 'observations';
      const isGaps = key.toLowerCase() === 'gaps';
      const isRecommendations = key.toLowerCase() === 'recommendations';
      const isComplianceStatus = key.toLowerCase().includes('compliance');

      // Mandatory response fields that should be locked
      const isMandatoryResponseField = isReferences || isObservations || isGaps || isRecommendations || isComplianceStatus;

      return {
        id: key.toLowerCase().replace(/\s+/g, '_'),
        key: key,
        ui_key: key,
        isMandatory: index < 3 || isMandatoryResponseField, // First 3 fields + mandatory response fields
        isEditable:
          index >= 3 &&
          !isMandatoryResponseField, // Fields after the first 3 can be edited unless they're mandatory response fields
        locked: isMandatoryResponseField, // Lock all mandatory response fields
        isFromStepThree: isFromStepThree, // Only mark as Step 3 if it actually exists in controls
      };
    });
  }, [fetchedResponseSchemaData, isResponseSchemaSuccess, controls]);

  // Initialize response fields
  useEffect(() => {
    if (isReorderingRef.current) {
      // Skip updating responseFields if a reorder is in progress
      return;
    }
    if (isResponseSchemaSuccess && transformedResponseFields.length > 0) {
      // Use the transformed fields from the fetched schema
      setResponseFields(transformedResponseFields);
      updateStepFourData({ responseFields: transformedResponseFields });
    } else if (!stepFourData.responseFields) {
      // Fallback to default fields if no schema is available
      const defaultFields: FrameworkField[] = [
        {
          id: 'control_id',
          key: 'Control ID',
          ui_key: 'Control ID',
          isMandatory: true,
          isEditable: false,
        },
        {
          id: 'control_description',
          key: 'Control Description',
          ui_key: 'Control Description',
          isMandatory: true,
          isEditable: false,
        },
        {
          id: 'testing_procedures',
          key: 'Testing Procedures',
          ui_key: 'Testing Procedures',
          isMandatory: true,
          isEditable: false,
        },
        {
          id: 'compliance_status',
          key: 'Compliance Status',
          ui_key: 'Compliance Status',
          isMandatory: true,
          isEditable: false,
          locked: true,
        },
        {
          id: 'observations',
          key: 'Observations',
          ui_key: 'Observations',
          isMandatory: true,
          isEditable: false,
          locked: true,
        },
        {
          id: 'gaps',
          key: 'Gaps',
          ui_key: 'Gaps',
          isMandatory: true,
          isEditable: false,
          locked: true,
        },
        {
          id: 'recommendations',
          key: 'Recommendations',
          ui_key: 'Recommendations',
          isMandatory: true,
          isEditable: false,
          locked: true,
        },
        {
          id: 'references',
          key: 'References',
          ui_key: 'References',
          isMandatory: true,
          isEditable: false,
          locked: true,
        },
        {
          id: 'test_results',
          key: 'Test Results',
          ui_key: 'Test Results',
          isMandatory: false,
          isEditable: true,
        },
      ];

      setResponseFields(defaultFields);
      updateStepFourData({ responseFields: defaultFields });
    } else {
      // Use existing fields from stepFourData
      setResponseFields(stepFourData.responseFields);
    }
  }, [
    isResponseSchemaSuccess,
    transformedResponseFields,
    stepFourData.responseFields,
    updateStepFourData,
  ]);

  // Add fieldEditStates state object to manage editing state and editTitle for each field
  const [fieldEditStates, setFieldEditStates] = useState<
    Record<string, { isEditing: boolean; editTitle: string }>
  >(() => {
    const obj: Record<string, { isEditing: boolean; editTitle: string }> = {};
    (stepFourData.responseFields || []).forEach((f: FrameworkField) => {
      obj[f.id] = { isEditing: false, editTitle: f.ui_key };
    });
    return obj;
  });

  // Keep fieldEditStates in sync with responseFields
  useEffect(() => {
    setFieldEditStates((prev) => {
      const next = { ...prev };
      (responseFields || []).forEach((f: FrameworkField) => {
        if (!next[f.id]) {
          next[f.id] = { isEditing: false, editTitle: f.ui_key };
        }
      });
      // Remove deleted fields
      Object.keys(next).forEach((id) => {
        if (!(responseFields || []).find((f: FrameworkField) => f.id === id)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [responseFields]);

  // Get response schema sample data for preview
  const responseSchemaData = useMemo(() => {
    return fetchedResponseSchemaData || [];
  }, [fetchedResponseSchemaData]);

  // Helper function to refresh data after successful operations
  const refreshResponseSchemaData = useCallback(async () => {
    if (stepFourData.responseSchemaUrl) {
      // Invalidate the query to force a fresh fetch
      await queryClient.invalidateQueries({
        queryKey: [
          'framework',
          'response-schema',
          stepFourData.responseSchemaUrl,
        ],
      });

      // Also trigger a manual refetch
      await refetchResponseSchema();
    }
  }, [queryClient, stepFourData.responseSchemaUrl, refetchResponseSchema]);

  // Ref for the scrollable table container
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Add field handler
  const handleAddField = useCallback(async () => {
    setIsAddingField(true);

    // If there's a pending save operation, wait for it to complete
    if (isSaving) {
      // Wait for a short delay and check again
      let waitCount = 0;
      const maxWaitCount = 50; // Maximum number of 100ms intervals to wait (5 seconds total)

      while (isSaving && waitCount < maxWaitCount) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
      }

      // If still saving after max wait, notify user
      if (isSaving) {
        addNotification({
          type: 'info',
          message: 'Still saving previous changes. Please try again in a moment.',
        });
        setIsAddingField(false);
        return;
      }
    }

    // Generate a unique field name that doesn't conflict
    const timestamp = Date.now();
    const newFieldId = `field_${timestamp}`;
    const tempFieldName = `New Field ${timestamp}`;

    const newField: FrameworkField = {
      id: newFieldId,
      key: tempFieldName, // Use unique temporary name
      ui_key: tempFieldName, // Use unique temporary name
      isMandatory: false,
      isEditable: true,
      isFromStepThree: false,
    };

    const updatedFields = [...responseFields, newField];
    setResponseFields(updatedFields);
    updateStepFourData({ responseFields: updatedFields });

    // Immediately set this field to editing mode
    setFieldEditStates((prev) => ({
      ...prev,
      [newFieldId]: { isEditing: true, editTitle: tempFieldName },
    }));

    try {
      // If we have response schema data, add the new field with empty values
      if (
        responseSchemaData &&
        responseSchemaData.length > 0 &&
        stepFourData.responseSchemaUrl
      ) {
        try {
          const updatedData = responseSchemaData.map((item: any) => ({
            ...item,
            [tempFieldName]: '', // Use the unique temporary name
          }));

          await saveNow(updatedData);
          await refreshResponseSchemaData();

          // Auto-scroll to the right after adding a field
          setTimeout(() => {
            if (tableScrollRef.current) {
              tableScrollRef.current.scrollTo({
                left: tableScrollRef.current.scrollWidth,
                behavior: 'smooth',
              });
            }
          }, 100);

        } catch (error) {
          console.error('Failed to add new field to schema:', error);
          addNotification({
            type: 'error',
            message: 'Failed to add new field',
          });
        }
      }
    } finally {
      setIsAddingField(false);
    }
  }, [
    responseFields,
    responseSchemaData,
    stepFourData.responseSchemaUrl,
    updateStepFourData,
    saveNow,
    refreshResponseSchemaData,
    isSaving,
  ]);

  // Update field title handler
  const handleUpdateFieldTitle = useCallback(
    async (fieldId: string, newTitle: string) => {
      const oldField = responseFields.find((f) => f.id === fieldId);
      if (!oldField) return;

      const updatedFields = responseFields.map((field) =>
        field.id === fieldId
          ? { ...field, ui_key: newTitle, key: newTitle }
          : field
      );
      setResponseFields(updatedFields);
      updateStepFourData({ responseFields: updatedFields });

      // If we have response schema data, update the field names and upload immediately
      if (
        responseSchemaData &&
        responseSchemaData.length > 0 &&
        stepFourData.responseSchemaUrl
      ) {
        try {
          // Update field names in the response schema data
          const updatedData = responseSchemaData.map((item: any) => {
            const updatedItem: any = {};
            Object.keys(item).forEach((key) => {
              if (key === oldField.key || key === oldField.ui_key) {
                // Rename the field
                updatedItem[newTitle] = item[key];
              } else {
                updatedItem[key] = item[key];
              }
            });
            return updatedItem;
          });

          // Use the auto-save hook to upload immediately
          await saveNow(updatedData);

          // Refresh the data from server to update UI
          await refreshResponseSchemaData();
        } catch (error) {
          console.error('Failed to update response field name:', error);
          addNotification({
            type: 'error',
            message: 'Failed to update response field name',
          });
        }
      }
    },
    [
      responseFields,
      responseSchemaData,
      stepFourData.responseSchemaUrl,
      updateStepFourData,
      saveNow,
      refreshResponseSchemaData,
    ]
  );

  // Delete field handler
  const handleDeleteField = useCallback(
    (fieldId: string) => {
      const fieldToDelete = responseFields.find((f) => f.id === fieldId);
      if (!fieldToDelete) return;

      // Prevent deletion of mandatory or locked fields
      if (fieldToDelete.isMandatory || fieldToDelete.locked) {
        addNotification({
          type: 'error',
          message:
            'This field cannot be deleted as it is required for the framework.',
        });
        return;
      }

      // Add fade-out effect first (mark active deleting field)
      setIsDeleting(true);
      setActiveDeletingFieldId(fieldId);

      // Remove after brief animation (50ms for snappy feel)
      setTimeout(() => {
        // INSTANT UI UPDATE - Remove field immediately
        const updatedFields = responseFields.filter(
          (field) => field.id !== fieldId
        );
        setResponseFields(updatedFields);
        updateStepFourData({ responseFields: updatedFields });
      }, 50);

      // Background sync - don't block UI, fire and forget
      if (
        responseSchemaData &&
        responseSchemaData.length > 0 &&
        stepFourData.responseSchemaUrl
      ) {
        // Use queueMicrotask for immediate execution after current call stack
        queueMicrotask(async () => {
          try {
            const updatedData = responseSchemaData.map((item: any) => {
              const newItem = { ...item };

              // Find the exact field name that exists in the data
              const fieldNameToDelete = Object.keys(newItem).find((key) => {
                // Try exact matches first
                if (key === fieldToDelete.key || key === fieldToDelete.ui_key) {
                  return true;
                }

                // Try case-insensitive matches
                if (
                  key.toLowerCase() === fieldToDelete.key.toLowerCase() ||
                  key.toLowerCase() === fieldToDelete.ui_key.toLowerCase()
                ) {
                  return true;
                }

                // Try normalized matches (spaces to underscores, etc.)
                const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
                const normalizedFieldKey = fieldToDelete.key
                  .toLowerCase()
                  .replace(/\s+/g, '_');
                const normalizedFieldUiKey = fieldToDelete.ui_key
                  .toLowerCase()
                  .replace(/\s+/g, '_');

                if (
                  normalizedKey === normalizedFieldKey ||
                  normalizedKey === normalizedFieldUiKey ||
                  normalizedKey === fieldToDelete.id
                ) {
                  return true;
                }

                return false;
              });

              if (fieldNameToDelete) {
                delete newItem[fieldNameToDelete];
              }

              return newItem;
            });

            // Background save - don't await UI, keep spinner until it completes
            saveNow(updatedData)
              .then(() => {
                // Only refresh if save was successful
                return refreshResponseSchemaData();
              })
              .catch((error) => {
                console.error('Failed to delete field from schema:', error);
                // Silent fail - don't revert UI since user already sees field as deleted
                // Optionally notify user
              })
              .finally(() => {
                // Clear deleting indicator only after background sync finishes
                setIsDeleting(false);
                setActiveDeletingFieldId(null);
              });
          } catch (error) {
            console.error('Error in background delete sync:', error);
            // Silent fail - don't revert UI
          }
        });
      }
      else {
        // No background sync needed; clear deleting indicator after removal
        setTimeout(() => {
          setIsDeleting(false);
          setActiveDeletingFieldId(null);
        }, 50);
      }
    },
    [
      responseFields,
      responseSchemaData,
      stepFourData.responseSchemaUrl,
      updateStepFourData,
      saveNow,
      refreshResponseSchemaData,
    ]
  );

  const handleDragStart = (fieldId: string) => {
    const field = responseFields.find(f => f.id === fieldId);
    // Only allow drag if field exists and is not locked or from Step 3
    if (field && !field.isFromStepThree) {
      setDraggedFieldId(fieldId);
    }
  };

  const handleDragEnd = () => {
    setDraggedFieldId(null);
  };

  const handleDragOver = (e: React.DragEvent, targetFieldId: string) => {
    e.preventDefault();
  };

  // Function to reorder response schema data based on field order
  const reorderResponseSchemaData = (
    originalData: any[],
    fieldOrder: FrameworkField[]
  ) => {
    if (!originalData || originalData.length === 0) return originalData;

    return originalData.map((item) => {
      const reorderedItem: any = {};

      // Add fields in the new order
      fieldOrder.forEach((field) => {
        const key = field.key;
        if (key in item) {
          reorderedItem[key] = item[key];
        }
      });

      // Add any remaining fields that weren't in the field order
      Object.keys(item).forEach((key) => {
        if (!(key in reorderedItem)) {
          reorderedItem[key] = item[key];
        }
      });

      return reorderedItem;
    });
  };

  const handleDrop = useCallback(
    async (targetFieldId: string) => {
      if (!draggedFieldId || draggedFieldId === targetFieldId) return;

      const draggedField = responseFields.find((f) => f.id === draggedFieldId);
      const targetField = responseFields.find((f) => f.id === targetFieldId);

      // Don't allow dragging Step 3 fields (from controls), mandatory fields, or locked fields
      if (!draggedField || !targetField) return;

      // Prevent dragging if:
      // 1. The dragged field is from Step 3
      // 2. The dragged field is locked
      // 3. The target field is from Step 3
      if (draggedField.isFromStepThree || targetField.isFromStepThree) {
        return;
      }
      document.body.style.cursor = "default";

      // Reorder fields immediately for UI update
      isReorderingRef.current = true;
      const updatedFields = [...responseFields];
      const draggedIndex = updatedFields.findIndex(
        (f) => f.id === draggedFieldId
      );
      const targetIndex = updatedFields.findIndex(
        (f) => f.id === targetFieldId
      );

      // Remove dragged item
      const [removed] = updatedFields.splice(draggedIndex, 1);
      // Insert at new position
      updatedFields.splice(targetIndex, 0, removed);

      // Update UI immediately
      setResponseFields(updatedFields);
      updateStepFourData({ responseFields: updatedFields });
      setDraggedFieldId(null);

      // Handle server sync separately (don't block UI update)
      if (
        responseSchemaData &&
        responseSchemaData.length > 0 &&
        stepFourData.responseSchemaUrl
      ) {
        try {
          // Reorder the response schema data based on new field order
          const reorderedData = reorderResponseSchemaData(
            responseSchemaData,
            updatedFields
          );

          // Use the auto-save hook to upload directly to backend URL
          await saveNow(reorderedData);

          // Only refresh if save was successful
          await refreshResponseSchemaData();
        } catch (error) {
          console.error('Failed to update response schema order:', error);
          addNotification({
            type: 'error',
            message: 'Failed to update response field order',
          });

          // Revert UI changes if server sync failed
          setResponseFields(responseFields);
          updateStepFourData({ responseFields: responseFields });
        } finally {
          isReorderingRef.current = false;
        }
      }
    },
    [
      draggedFieldId,
      responseFields,
      responseSchemaData,
      stepFourData.responseSchemaUrl,
      updateStepFourData,
      saveNow,
      refreshResponseSchemaData,
      reorderResponseSchemaData,
    ]
  );

  const getFieldData = (fieldId: string, rowIndex: number) => {
    // First check if we have response schema data to preview
    if (responseSchemaData.length > 0 && responseSchemaData[rowIndex]) {
      const schemaItem = responseSchemaData[rowIndex];

      // Find matching field by key or ui_key
      const field = responseFields.find((f) => f.id === fieldId);
      if (field) {
        return schemaItem[field.key] || schemaItem[field.ui_key] || '';
      }
    }

    // Fallback to controls data for display purposes
    const control = controls[rowIndex];
    if (!control) return '';

    // Map framework data to display format
    switch (fieldId) {
      case 'control_id':
      case 'id':
        return control.id;
      case 'control_description':
        return control['Control Description'] || control.description || '';
      case 'testing_procedures':
        return control['Testing Procedures'] || '';
      case 'compliance_status':
        return ''; // Default value for compliance status
      case 'test_results':
        return '';
      default:
        // Check if this field exists directly in the control object
        if (fieldId in control) {
          return control[fieldId] || '';
        }

        // Check for case variations
        const normalizedFieldId = fieldId.toLowerCase();
        for (const key in control) {
          if (key.toLowerCase() === normalizedFieldId) {
            return control[key] || '';
          }
        }

        return '';
    }
  };

  // Filter FIELD_TYPE_OPTIONS based on availability of testing procedures
  const availableFieldTypeOptions = useMemo(() => {
    return FIELD_TYPE_OPTIONS.filter(
      option => hasTestingProcedures || option.value !== 'test_results'
    );
  }, [hasTestingProcedures]);

  // Memoize used field types for each field to avoid expensive computation in render
  const fieldTypesByFieldId = useMemo(() => {
    const result: Record<string, string[]> = {};
    responseFields.forEach((field) => {
      result[field.id] = responseFields
        .filter((f) => f.id !== field.id)
        .map((f) => {
          const match = availableFieldTypeOptions.find(
            (opt) => opt.label === f.ui_key || opt.label === f.key
          );
          return match?.value;
        })
        .filter(Boolean) as string[];
    });
    return result;
  }, [responseFields, availableFieldTypeOptions]);

  // Compute canAddField: true if at least one available FIELD_TYPE_OPTIONS value is not used
  const usedDropdownFieldTypes = responseFields
    .filter((f) =>
      availableFieldTypeOptions.some(
        (opt) => opt.label === f.ui_key || opt.label === f.key
      )
    )
    .map((f) => {
      const match = availableFieldTypeOptions.find(
        (opt) => opt.label === f.ui_key || opt.label === f.key
      );
      return match?.value;
    })
    .filter(Boolean);

  const canAddField = availableFieldTypeOptions.some(
    (opt) => !usedDropdownFieldTypes.includes(opt.value)
  );

  // Show loading state if we're waiting for response schema
  if (
    stepFourData.responseSchemaUrl &&
    !isResponseSchemaSuccess &&
    responseFields.length === 0
  ) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4">
        <Spinner size="lg" />
        <div className="text-sm text-[#71717A]">Loading response schema...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center p-0 gap-12 w-full h-full bg-[#FEFEFE]">
      <div className="flex flex-col items-center w-full h-full gap-4 p-0">
        {/* Header Section */}
        <div className="flex flex-row items-center justify-between w-full gap-2 p-0">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-[#18181B]">
              Define the response format.
            </p>
          </div>
          <div className="flex flex-row items-center gap-3">
            <button
              className="flex flex-row items-center py-1 px-2 gap-1.5 bg-[#FAFAFA] border border-[#006FEE] rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleAddField}
              disabled={isAddingField}
            >
              <div className="text-xs font-medium text-[#006FEE] flex items-center gap-1">Add Field {isAddingField && <Spinner size='sm' classNames={{ wrapper: "h-4 w-4" }} />}</div>

            </button>
          </div>
        </div>

        {/* Main Table */}
        <div className="mb-16 flex flex-col items-center p-0 w-full bg-white border border-[#E4E4E7] rounded-lg overflow-hidden flex-1">
          <div
            ref={tableScrollRef}
            className="flex flex-row items-start flex-1 w-full min-w-0 min-h-0 overflow-x-auto static-thin-scrollbar"
            style={{
              scrollBehavior: 'smooth',
              willChange: 'scroll-position',
            }}
          >
            {responseFields.map((field: FrameworkField) => {
              // Get precomputed usedFieldTypes for this field
              const usedFieldTypes = fieldTypesByFieldId[field.id] || [];
              const isDeleting = isDeletingField && activeDeletingFieldId === field.id;

              return (
                <div
                  key={field.id}
                >
                  <Column
                    header={
                      <DraggableHeader
                        title={field.ui_key.startsWith('New Field ') ? 'Select Response Type' : field.ui_key}
                        isEditable={field.isEditable}
                        isDeletable={
                          !field.isMandatory &&
                          !field.locked &&
                          !field.isFromStepThree
                        }
                        locked={field.locked}
                        showHandle={!field.isFromStepThree}
                        isDeleting={isDeleting}
                        fieldId={field.id}
                        onTitleChange={(newTitle) =>
                          handleUpdateFieldTitle(field.id, newTitle)
                        }
                        onDelete={() => handleDeleteField(field.id)}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, field.id)}
                        onDrop={handleDrop}
                        usedFieldTypes={usedFieldTypes}
                        hasTestingProcedures={hasTestingProcedures}
                      />
                    }

                  >
                    {/* Show data from response schema if available, otherwise use controls data */}
                    {(responseSchemaData.length > 0
                      ? responseSchemaData
                      : controls
                    ).map((_: any, rowIndex: number) => (
                      <TableCell
                        key={rowIndex}
                        className={`${field.isFromStepThree ? 'bg-[#f1f1f1]' : ''} ${responseFields[responseFields.length - 1].id !== field.id ? 'border-r border-[#E4E4E7]' : ''}`}
                      >
                        <span
                          className={`h-24 py-3 px-3 text-sm w-full overflow-hidden line-clamp-4 ${field.id === 'control_category' ? 'text-center' : ''} text-[#71717A] break-words`}
                        >
                          {getFieldData(field.id, rowIndex)}
                        </span>
                      </TableCell>
                    ))}
                  </Column>
                </div>
              );
            })}

            {/* Add placeholder columns to reach a total of 5 */}
            {responseFields.length < 5 &&
              Array.from({ length: 5 - responseFields.length }).map((_, i) => (
                <div
                  className="flex flex-col items-start min-w-[250px] flex-shrink-0 h-full"
                  key={i}
                >
                  <EditableHeader
                    fieldId={`placeholder_${i}`}
                    value={''}
                    isEditing={false}
                    onEditStart={canAddField ? handleAddField : () => { }}
                    onEditEnd={() => { }}
                    onEditChange={() => { }}
                  />
                  {(responseSchemaData.length > 0
                    ? responseSchemaData
                    : controls
                  ).map((_: any, index: number) => (
                    <TableCell key={index}>
                      <span className="h-24 text-sm text-[#3F3F46] w-full"></span>
                    </TableCell>
                  ))}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
